import { useState, useCallback, useRef, useEffect } from 'react';
import { createApiHeaders, apiFetch } from '../../../../config/api';
import { REQUEST_TIMEOUT_LLM_EXECUTION } from '../../../../config/constants';

/**
 * Preflight execution threshold (in seconds)
 * If estimated execution time is below this, use sync execution
 * If above, use async execution with polling
 */
const PREFLIGHT_SYNC_THRESHOLD = 20; // seconds

/**
 * Custom hook for preflight-first template execution
 *
 * Flow:
 * 1. Run preflight validation first
 * 2. Get estimated execution time
 * 3. If estimated_time < 20s → Use sync execution
 * 4. If estimated_time >= 20s → Use async execution with polling
 *
 * @param {string} templateId - Template ID
 * @param {string} templateName - Optional template name for logging
 * @param {Function} onExecutingChange - Optional callback when executing state changes
 * @returns {Object} Execution state and functions
 */
export const usePreflightExecution = (
  templateId,
  templateName = null,
  onExecutingChange = null
) => {
  const [preflightStatus, setPreflightStatus] = useState(null); // 'validating', 'complete', 'error'
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [executionMode, setExecutionMode] = useState(null); // 'sync', 'async', null
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionStatus, setExecutionStatus] = useState(null); // Current execution status message
  const [executionProgress, setExecutionProgress] = useState(null); // Progress info (e.g., estimated seconds remaining)

  // Use refs to store stable function references to avoid circular dependency issues
  const executeSyncRef = useRef(null);
  const executeAsyncRef = useRef(null);

  /**
   * Execute template synchronously (for fast executions)
   */
  const executeSync = useCallback(
    async (payload, headers, executionId) => {
      // Use simplified endpoint for sync execution (faster, no job overhead)
      const syncPayload = {
        ...payload,
        allow_draft:
          payload.allow_draft !== undefined ? payload.allow_draft : true,
      };

      const result = await apiFetch(
        `/api/v1/templates/executions/${templateId}/execute-simplified`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(syncPayload),
          timeout: REQUEST_TIMEOUT_LLM_EXECUTION, // 300 seconds
        }
      );

      return result;
    },
    [templateId]
  );

  /**
   * Execute template asynchronously with polling (for slow executions)
   * @param {Function} onStatusUpdate - Optional callback for status updates (status, progress)
   */
  const executeAsync = useCallback(
    async (payload, headers, executionId, onStatusUpdate = null) => {
      // Use advanced endpoint with async_mode
      const asyncPayload = {
        template_id: templateId,
        template_name: templateName || undefined,
        input_data: payload.input_data || {},
        version_number: payload.version_number || undefined,
        allow_draft:
          payload.allow_draft !== undefined ? payload.allow_draft : true,
        async_mode: true,
        llm_config: payload.llm_config || undefined,
        metadata: payload.metadata || {},
      };

      // Remove undefined values
      Object.keys(asyncPayload).forEach(
        key => asyncPayload[key] === undefined && delete asyncPayload[key]
      );

      // Create async job
      const jobResponse = await apiFetch(`/api/v1/templates/advanced/execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify(asyncPayload),
        timeout: REQUEST_TIMEOUT_LLM_EXECUTION, // 300 seconds
      });

      if (!jobResponse.job_id) {
        throw new Error('Async execution failed: No job_id returned');
      }

      // Poll for job status with fixed 3 second interval (no exponential backoff)
      const jobId = jobResponse.job_id;
      const pollDelay = 3000; // Fixed 3 seconds interval
      const maxPollTime = 330000; // 5.5 minutes (330 seconds) - matches backend job timeout of 300s + 30s buffer
      const startTime = Date.now();

      while (Date.now() - startTime < maxPollTime) {
        await new Promise(resolve => setTimeout(resolve, pollDelay));

        // Use apiFetch instead of fetch to ensure authentication headers are included
        const statusData = await apiFetch(
          `/api/v1/templates/advanced/jobs/${jobId}/status`,
          {
            method: 'GET',
            headers,
            timeout: 10000, // 10 seconds timeout for status checks
          }
        );

        // Update status based on job status
        if (onStatusUpdate) {
          const statusMessages = {
            pending: 'Job queued, waiting to start...',
            processing: 'Processing template execution...',
            retrying: 'Retrying execution...',
            completed: 'Execution completed',
            failed: 'Execution failed',
            timeout: 'Execution timeout',
            cancelled: 'Execution cancelled',
          };
          const statusMessage =
            statusMessages[statusData.status] || 'Processing...';
          const progress = statusData.estimated_seconds_remaining
            ? {
                estimatedSecondsRemaining:
                  statusData.estimated_seconds_remaining,
              }
            : null;
          onStatusUpdate(statusMessage, progress);
        }

        if (statusData.status === 'completed') {
          return statusData.result;
        } else if (
          statusData.status === 'failed' ||
          statusData.status === 'timeout'
        ) {
          throw new Error(statusData.error || `Job ${statusData.status}`);
        }

        // Fixed interval - continue polling with 3 second delay
      }

      throw new Error('Job polling timeout - request took too long');
    },
    [templateId, templateName]
  );

  // Update refs when functions change
  useEffect(() => {
    executeSyncRef.current = executeSync;
    executeAsyncRef.current = executeAsync;
  }, [executeSync, executeAsync]);

  /**
   * Execute template with preflight-first logic
   * @param {Object} payload - Execution payload (input_data, version_number, etc.)
   * @param {Function} onPreflightComplete - Optional callback when preflight completes
   * @returns {Promise<Object>} Execution result
   */
  const executeWithPreflight = useCallback(
    async (payload, onPreflightComplete = null) => {
      if (!templateId) {
        throw new Error('Template ID is required');
      }

      const headers = createApiHeaders();

      try {
        // Set executing state
        setIsExecuting(true);
        if (onExecutingChange) {
          onExecutingChange(true);
        }

        // Step 1: Run preflight validation
        setPreflightStatus('validating');
        setEstimatedTime(null);
        setExecutionMode(null);
        setExecutionStatus('Validating preflight...');
        setExecutionProgress(null);

        const preflightPayload = {
          input_data: payload.input_data || {},
          version: payload.version_number || undefined,
          allow_draft:
            payload.allow_draft !== undefined ? payload.allow_draft : true,
          save_preflight: true, // Save preflight result for potential resume
          llm_config: payload.llm_config || undefined,
        };

        // Remove undefined values
        Object.keys(preflightPayload).forEach(
          key =>
            preflightPayload[key] === undefined && delete preflightPayload[key]
        );

        const preflightResponse = await apiFetch(
          `/api/v1/templates/executions/preflight/${templateId}`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(preflightPayload),
            timeout: 30000, // 30 seconds for preflight (should be fast)
          }
        );

        if (!preflightResponse.valid) {
          const errors = preflightResponse.validation_errors || [];
          const adapterError = preflightResponse.adapter_error;

          // Extract error messages from validation_errors array
          // Validation errors are objects with structure: {level, error, type, ...}
          // Example: {level: "pre_execution", error: "Adapter configuration failed: ...", type: "adapter_config_error"}
          const errorMessages = errors.map(error => {
            if (typeof error === 'string') {
              return error;
            }
            if (typeof error === 'object' && error !== null) {
              // Priority order: error field (most common), then message, detail, msg
              // Also include type for context if available
              const errorText =
                error.error ||
                error.message ||
                error.detail ||
                error.msg ||
                JSON.stringify(error);

              // Add type context if available and different from error text
              if (error.type && error.type !== errorText) {
                return `${error.type}: ${errorText}`;
              }
              return errorText;
            }
            return String(error);
          });

          // Combine validation errors and adapter error
          const allErrors = [...errorMessages];
          if (adapterError) {
            allErrors.push(`Adapter error: ${adapterError}`);
          }

          const errorMessage =
            allErrors.length > 0
              ? `Preflight validation failed: ${allErrors.join(', ')}`
              : 'Preflight validation failed';
          setPreflightStatus('error');
          throw new Error(errorMessage);
        }

        // Step 2: Extract estimated time
        const estimated = preflightResponse.estimated_time;
        setEstimatedTime(estimated);
        setPreflightStatus('complete');

        // Call optional callback
        if (onPreflightComplete) {
          onPreflightComplete({
            estimated_time: estimated,
            execution_id: preflightResponse.execution_id,
            preflight_result: preflightResponse.preflight_result,
          });
        }

        // Step 3: Route to sync or async based on estimated time
        const useSync =
          estimated !== null && estimated < PREFLIGHT_SYNC_THRESHOLD;
        setExecutionMode(useSync ? 'sync' : 'async');

        let result;
        if (useSync) {
          // Sync execution: Call execution endpoint directly
          setExecutionStatus('Executing template (sync mode)...');
          setExecutionProgress(null);
          result = await executeSyncRef.current(
            payload,
            headers,
            preflightResponse.execution_id
          );
        } else {
          // Async execution: Use async mode with polling
          setExecutionStatus('Executing template (async mode)...');
          setExecutionProgress(null);
          result = await executeAsyncRef.current(
            payload,
            headers,
            preflightResponse.execution_id,
            (status, progress) => {
              // Callback to update status during async polling
              setExecutionStatus(status);
              setExecutionProgress(progress);
            }
          );
        }

        // Clear executing state
        setIsExecuting(false);
        setExecutionStatus(null);
        setExecutionProgress(null);
        if (onExecutingChange) {
          onExecutingChange(false);
        }

        return result;
      } catch (err) {
        setPreflightStatus('error');
        setIsExecuting(false);
        setExecutionStatus(null);
        setExecutionProgress(null);
        if (onExecutingChange) {
          onExecutingChange(false);
        }
        throw err;
      }
    },
    [templateId, onExecutingChange]
  );

  /**
   * Reset preflight status
   */
  const resetPreflight = useCallback(() => {
    setPreflightStatus(null);
    setEstimatedTime(null);
    setExecutionMode(null);
    setExecutionStatus(null);
    setExecutionProgress(null);
  }, []);

  return {
    executeWithPreflight,
    preflightStatus, // 'validating', 'complete', 'error', null
    estimatedTime, // number | null
    executionMode, // 'sync', 'async', null
    isExecuting, // boolean - execution in progress
    executionStatus, // string | null - current status message
    executionProgress, // object | null - progress info (e.g., { estimatedSecondsRemaining })
    resetPreflight,
    PREFLIGHT_SYNC_THRESHOLD, // Export threshold for UI display
  };
};
